<template>
  <div class="page-content almacenes-page">
    <div class="almacenes-wrapper">
      <div class="almacenes-card">
        <div class="almacenes-header">
          <div>
            <p class="almacenes-title">Almacenes</p>
            <p class="almacenes-subtitle">
              Administra almacenes y sus ubicaciones como una estructura de niveles: cuarto, estante, repisa, tarima o el criterio que necesites.
            </p>
          </div>

          <div class="almacenes-header-actions">
            <input
              v-model.trim="busqueda"
              type="text"
              class="almacenes-search"
              placeholder="Buscar almacén o ubicación…"
            />
            <button type="button" class="ios-primary-button" @click="abrirCrearAlmacen">
              + Almacén
            </button>
          </div>
        </div>

        <div v-if="cargando" class="almacenes-empty">Cargando almacenes…</div>

        <section v-else class="almacenes-grid">
          <aside class="almacenes-list">
            <button
              v-for="almacen in almacenesFiltrados"
              :key="almacen.id_almacen"
              type="button"
              class="almacen-item"
              :class="{ 'almacen-item--active': Number(almacenSeleccionado?.id_almacen) === Number(almacen.id_almacen) }"
              @click="seleccionarAlmacen(almacen)"
            >
              <div class="almacen-item__main">
                <div class="almacen-item__avatar">{{ iniciales(almacen.nombre) }}</div>
                <div>
                  <p class="almacen-item__name">{{ almacen.nombre }}</p>
                  <p class="almacen-item__desc">{{ almacen.descripcion || 'Sin descripción.' }}</p>
                </div>
              </div>
              <div class="almacen-item__meta">
                <span class="chip-soft">{{ almacen.total_ubicaciones || 0 }} ubicaciones</span>
                <button type="button" class="icon-btn" @click.stop="editarAlmacen(almacen)">✏️</button>
              </div>
            </button>

            <div v-if="!almacenesFiltrados.length" class="almacenes-empty">
              No hay almacenes registrados.
            </div>
          </aside>

          <section class="ubicaciones-panel">
            <div class="ubicaciones-panel__head">
              <div>
                <p class="ubicaciones-panel__title">
                  {{ almacenSeleccionado ? ('Ubicaciones de ' + almacenSeleccionado.nombre) : 'Ubicaciones de almacén' }}
                </p>
                <p class="ubicaciones-panel__subtitle">
                  Primero eliges el almacén de la izquierda. Después aquí construyes o consultas la ruta de cada ubicación.
                </p>
              </div>

              <button
                v-if="almacenSeleccionado"
                type="button"
                class="ios-primary-button"
                @click="abrirCrearUbicacion"
              >
                + Ubicación
              </button>
            </div>

            <div v-if="!almacenSeleccionado" class="almacenes-empty">
              Selecciona un almacén en la izquierda para administrar sus ubicaciones.
            </div>

            <div v-else-if="!ubicacionesFiltradas.length" class="almacenes-empty">
              Este almacén aún no tiene ubicaciones registradas.
            </div>

            <div v-else class="ubicaciones-folders">
              <article
                v-for="grupo in gruposNivel1"
                :key="grupo.nombre"
                class="folder-card"
              >
                <div class="folder-card__head">
                  <div>
                    <p class="folder-card__title">{{ grupo.nombre }}</p>
                    <p class="folder-card__subtitle">{{ grupo.items.length }} rutas registradas</p>
                  </div>
                </div>

                <div class="folder-card__body">
                  <button
                    v-for="ubicacion in grupo.items"
                    :key="ubicacion.id_ubicacion_almacen"
                    type="button"
                    class="folder-route"
                    @click="editarUbicacion(ubicacion)"
                  >
                    <span class="folder-route__path">
                      {{ rutaSinAlmacen(ubicacion) }}
                    </span>
                    <span class="folder-route__meta">Editar</span>
                  </button>
                </div>
              </article>
            </div>
          </section>
        </section>
      </div>
    </div>

    <div v-if="modalAlmacen" class="overlay" @click.self="cerrarModalAlmacen">
      <div class="modal-card">
        <div class="modal-head">
          <p class="modal-title">{{ formAlmacen.id_almacen ? 'Editar almacén' : 'Nuevo almacén' }}</p>
          <button type="button" class="modal-close" @click="cerrarModalAlmacen">✕</button>
        </div>
        <div class="modal-body">
          <div class="field">
            <label>Nombre</label>
            <input v-model.trim="formAlmacen.nombre" class="input" placeholder="Ej. Matriz / Central / Principal" />
          </div>
          <div class="field">
            <label>Descripción</label>
            <textarea v-model.trim="formAlmacen.descripcion" class="input input--textarea" rows="3" placeholder="Notas opcionales del almacén"></textarea>
          </div>
        </div>
        <div class="modal-actions">
          <button type="button" class="ios-ghost-button" @click="cerrarModalAlmacen">Cancelar</button>
          <button type="button" class="ios-primary-button" @click="guardarAlmacen">Guardar</button>
        </div>
      </div>
    </div>

    <div v-if="modalUbicacion" class="overlay" @click.self="cerrarModalUbicacion">
      <div class="modal-card modal-card--wide">
        <div class="modal-head">
          <p class="modal-title">{{ editandoUbicacion ? 'Editar ubicación' : 'Nueva ubicación' }}</p>
          <button type="button" class="modal-close" @click="cerrarModalUbicacion">✕</button>
        </div>
        <div class="modal-body">
          <div class="field">
            <label>Almacén seleccionado</label>
            <div class="selected-warehouse">{{ almacenSeleccionado?.nombre || '—' }}</div>
          </div>

          <template v-if="!editandoUbicacion">
            <SelectorUbicacionAlmacen
              v-model="selectorUbicacion"
              :almacenes="almacenes"
              :almacen-fijo-id="almacenSeleccionado?.id_almacen || ''"
              :permitir-limpiar="false"
              :mostrar-referencia="true"
              @created="onUbicacionCreadaDesdeSelector"
            />
          </template>

          <template v-else>
            <div class="field-grid">
              <div class="field">
                <label>Nivel 1</label>
                <input v-model.trim="formUbicacion.nivel_1" class="input" placeholder="Ej. Estante A" />
              </div>
              <div class="field">
                <label>Nivel 2</label>
                <input v-model.trim="formUbicacion.nivel_2" class="input" placeholder="Opcional" />
              </div>
              <div class="field">
                <label>Nivel 3</label>
                <input v-model.trim="formUbicacion.nivel_3" class="input" placeholder="Opcional" />
              </div>
            </div>
            <div class="field">
              <label>Referencia adicional</label>
              <textarea v-model.trim="formUbicacion.referencia" class="input input--textarea" rows="2" placeholder="Notas opcionales"></textarea>
            </div>
          </template>
        </div>
        <div class="modal-actions" v-if="editandoUbicacion">
          <button type="button" class="ios-ghost-button" @click="cerrarModalUbicacion">Cancelar</button>
          <button type="button" class="ios-primary-button" @click="guardarUbicacionEditada">Guardar cambios</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SelectorUbicacionAlmacen from '@/components/almacen/almacenes/SelectorUbicacionAlmacen.vue';

export default {
  name: 'DashboardAlmacenes',
  components: { SelectorUbicacionAlmacen },
  data() {
    return {
      cargando: false,
      busqueda: '',
      almacenes: [],
      almacenSeleccionado: null,
      modalAlmacen: false,
      modalUbicacion: false,
      editandoUbicacion: false,
      selectorUbicacion: { id_almacen: '', id_ubicacion_almacen: '' },
      formAlmacen: {
        id_almacen: null,
        nombre: '',
        descripcion: '',
      },
      formUbicacion: {
        id_ubicacion_almacen: null,
        id_almacen: '',
        nivel_1: '',
        nivel_2: '',
        nivel_3: '',
        referencia: '',
      },
    };
  },
  computed: {
    almacenesFiltrados() {
      const t = this.busqueda.toLowerCase();
      if (!t) return this.almacenes;
      return (this.almacenes || []).filter((a) => {
        const nombre = (a.nombre || '').toLowerCase();
        const descripcion = (a.descripcion || '').toLowerCase();
        const hitUbicacion = (a.ubicaciones || []).some((u) =>
          (u.ubicacion_label || '').toLowerCase().includes(t),
        );
        return nombre.includes(t) || descripcion.includes(t) || hitUbicacion;
      });
    },
    ubicacionesFiltradas() {
      const ubicaciones = this.almacenSeleccionado?.ubicaciones || [];
      const t = this.busqueda.toLowerCase();
      if (!t) return ubicaciones;
      return ubicaciones.filter((u) =>
        [u.nivel_1, u.nivel_2, u.nivel_3, u.ubicacion_label, u.referencia]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(t),
      );
    },
    gruposNivel1() {
      const groups = {};
      (this.ubicacionesFiltradas || []).forEach((u) => {
        const key = u.nivel_1 || 'Sin nivel 1';
        if (!groups[key]) groups[key] = [];
        groups[key].push(u);
      });
      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b, 'es'))
        .map((nombre) => ({
          nombre,
          items: groups[nombre].slice().sort((a, b) => this.rutaSinAlmacen(a).localeCompare(this.rutaSinAlmacen(b), 'es')),
        }));
    },
  },
  mounted() {
    this.cargar();
  },
  methods: {
    iniciales(nombre) {
      const parts = (nombre || '').split(' ').filter(Boolean);
      return ((parts[0]?.[0] || 'A') + (parts[1]?.[0] || '')).toUpperCase();
    },
    rutaSinAlmacen(ubicacion) {
      return [ubicacion.nivel_1, ubicacion.nivel_2, ubicacion.nivel_3].filter(Boolean).join(' → ');
    },
    async cargar() {
      this.cargando = true;
      try {
        const { data } = await axios.get('/api/almacen/almacenes/listar');
        this.almacenes = data?.data || [];
        if (!this.almacenSeleccionado && this.almacenes.length) {
          this.almacenSeleccionado = this.almacenes[0];
        } else if (this.almacenSeleccionado) {
          const actual = this.almacenes.find(
            (a) => Number(a.id_almacen) === Number(this.almacenSeleccionado.id_almacen),
          );
          this.almacenSeleccionado = actual || this.almacenes[0] || null;
        }
      } catch (error) {
        console.error('Error al cargar almacenes:', error);
        this.$toast?.error('No se pudieron cargar los almacenes.');
      } finally {
        this.cargando = false;
      }
    },
    seleccionarAlmacen(almacen) {
      this.almacenSeleccionado = almacen;
    },
    abrirCrearAlmacen() {
      this.formAlmacen = { id_almacen: null, nombre: '', descripcion: '' };
      this.modalAlmacen = true;
    },
    editarAlmacen(almacen) {
      this.formAlmacen = {
        id_almacen: almacen.id_almacen,
        nombre: almacen.nombre || '',
        descripcion: almacen.descripcion || '',
      };
      this.modalAlmacen = true;
    },
    cerrarModalAlmacen() {
      this.modalAlmacen = false;
    },
    async guardarAlmacen() {
      if (!this.formAlmacen.nombre) {
        this.$toast?.error('El nombre del almacén es obligatorio.');
        return;
      }
      const url = this.formAlmacen.id_almacen
        ? '/api/almacen/almacenes/actualizar'
        : '/api/almacen/almacenes/crear';
      try {
        const { data } = await axios.post(url, this.formAlmacen);
        if (data?.status === 'ok') {
          this.$toast?.success('Almacén guardado correctamente.');
          this.modalAlmacen = false;
          await this.cargar();
        }
      } catch (error) {
        console.error('Error al guardar almacén:', error);
        this.$toast?.error(error.response?.data?.message || 'No se pudo guardar el almacén.');
      }
    },
    abrirCrearUbicacion() {
      this.editandoUbicacion = false;
      this.formUbicacion = {
        id_ubicacion_almacen: null,
        id_almacen: this.almacenSeleccionado?.id_almacen || '',
        nivel_1: '',
        nivel_2: '',
        nivel_3: '',
        referencia: '',
      };
      this.selectorUbicacion = { id_almacen: this.almacenSeleccionado?.id_almacen || '', id_ubicacion_almacen: '' };
      this.modalUbicacion = true;
    },
    editarUbicacion(ubicacion) {
      this.editandoUbicacion = true;
      this.formUbicacion = {
        id_ubicacion_almacen: ubicacion.id_ubicacion_almacen,
        id_almacen: this.almacenSeleccionado?.id_almacen || '',
        nivel_1: ubicacion.nivel_1 || '',
        nivel_2: ubicacion.nivel_2 || '',
        nivel_3: ubicacion.nivel_3 || '',
        referencia: ubicacion.referencia || '',
      };
      this.modalUbicacion = true;
    },
    cerrarModalUbicacion() {
      this.modalUbicacion = false;
      this.editandoUbicacion = false;
    },
    async onUbicacionCreadaDesdeSelector() {
      this.$toast?.success('Ubicación guardada correctamente.');
      this.modalUbicacion = false;
      await this.cargar();
    },
    async guardarUbicacionEditada() {
      if (!this.formUbicacion.nivel_1) {
        this.$toast?.error('El nivel 1 es obligatorio.');
        return;
      }
      try {
        const { data } = await axios.post('/api/almacen/almacenes/ubicaciones/actualizar', this.formUbicacion);
        if (data?.status === 'ok') {
          this.$toast?.success('Ubicación actualizada correctamente.');
          this.modalUbicacion = false;
          await this.cargar();
        }
      } catch (error) {
        console.error('Error al actualizar ubicación:', error);
        this.$toast?.error(error.response?.data?.message || 'No se pudo actualizar la ubicación.');
      }
    },
  },
};
</script>

<style scoped>
.almacenes-page { min-height: 100%; }
.almacenes-wrapper { max-width: 1360px; margin: 0 auto; padding: 1.2rem; }
.almacenes-card {
  border-radius: 1.6rem;
  padding: 1.2rem;
  background: radial-gradient(circle at top left, rgba(148,163,184,.22), rgba(15,23,42,.96));
  border: 1px solid rgba(148,163,184,.28);
  box-shadow: 0 28px 70px rgba(2, 6, 23, 0.35);
}
.almacenes-header { display:flex; justify-content:space-between; gap:1rem; margin-bottom:1rem; }
.almacenes-title { margin:0; font-size:1.5rem; font-weight:800; color:#f8fafc; }
.almacenes-subtitle { margin:.3rem 0 0; color:rgba(226,232,240,.72); }
.almacenes-header-actions { display:flex; gap:.75rem; align-items:center; }
.almacenes-search, .input {
  width:100%; border-radius:1rem; border:1px solid rgba(148,163,184,.32); background:rgba(15,23,42,.62);
  color:#e2e8f0; padding:.82rem .95rem; outline:none;
}
.almacenes-search { min-width:280px; }
.almacenes-grid { display:grid; grid-template-columns: 380px minmax(0,1fr); gap:1rem; }
.almacenes-list, .ubicaciones-panel {
  border-radius:1.3rem; border:1px solid rgba(148,163,184,.22); background:rgba(15,23,42,.42); padding:1rem;
}
.almacenes-list { display:flex; flex-direction:column; gap:.75rem; max-height:72vh; overflow:auto; }
.almacen-item {
  width:100%; border:none; text-align:left; border-radius:1.15rem; background:rgba(30,41,59,.62); color:#e2e8f0;
  padding:.9rem; display:flex; flex-direction:column; gap:.8rem; cursor:pointer;
}
.almacen-item--active { outline:1px solid rgba(96,165,250,.48); box-shadow:0 0 0 3px rgba(59,130,246,.12) inset; }
.almacen-item__main { display:flex; gap:.8rem; align-items:flex-start; }
.almacen-item__avatar { width:2.3rem; height:2.3rem; border-radius:999px; display:flex; align-items:center; justify-content:center; background:rgba(59,130,246,.18); color:#bfdbfe; font-weight:800; }
.almacen-item__name { margin:0; font-weight:700; }
.almacen-item__desc { margin:.2rem 0 0; font-size:.83rem; color:rgba(226,232,240,.7); }
.almacen-item__meta { display:flex; justify-content:space-between; align-items:center; gap:.75rem; }
.ubicaciones-panel { min-height:72vh; }
.ubicaciones-panel__head { display:flex; justify-content:space-between; gap:1rem; margin-bottom:1rem; }
.ubicaciones-panel__title { margin:0; font-size:1.08rem; font-weight:800; color:#f8fafc; }
.ubicaciones-panel__subtitle { margin:.25rem 0 0; color:rgba(226,232,240,.68); }
.ubicaciones-folders { display:grid; grid-template-columns: repeat(auto-fill, minmax(270px, 1fr)); gap:.85rem; }
.folder-card { border-radius:1.1rem; border:1px solid rgba(148,163,184,.18); background:rgba(15,23,42,.48); padding:.8rem; }
.folder-card__head { margin-bottom:.65rem; }
.folder-card__title { margin:0; font-weight:800; color:#f8fafc; }
.folder-card__subtitle { margin:.2rem 0 0; font-size:.78rem; color:rgba(226,232,240,.65); }
.folder-card__body { display:flex; flex-direction:column; gap:.5rem; }
.folder-route {
  width:100%; border:none; border-radius:.95rem; padding:.7rem .75rem; text-align:left; cursor:pointer;
  background:rgba(30,41,59,.72); color:#e2e8f0; display:flex; justify-content:space-between; gap:.75rem;
}
.folder-route__path { font-size:.85rem; }
.folder-route__meta { font-size:.74rem; color:#93c5fd; }
.chip-soft {
  display:inline-flex; align-items:center; border-radius:999px; padding:.32rem .65rem; font-size:.74rem;
  background:rgba(59,130,246,.14); color:#dbeafe; border:1px solid rgba(96,165,250,.22);
}
.icon-btn { border:none; background:transparent; color:#cbd5e1; cursor:pointer; font-size:1rem; }
.almacenes-empty {
  border-radius:1rem; padding:1rem; color:rgba(226,232,240,.74); background:rgba(15,23,42,.32); text-align:center;
}
.overlay { position:fixed; inset:0; z-index:70; display:flex; align-items:center; justify-content:center; padding:1rem; background:rgba(2,6,23,.72); backdrop-filter:blur(10px); }
.modal-card {
  width:100%; max-width:760px; border-radius:1.5rem; background:radial-gradient(circle at top left, rgba(148,163,184,.18), rgba(15,23,42,.98));
  border:1px solid rgba(148,163,184,.28); padding:1rem;
}
.modal-card--wide { max-width:900px; }
.modal-head { display:flex; justify-content:space-between; gap:.75rem; align-items:center; margin-bottom:.8rem; }
.modal-title { margin:0; font-size:1.15rem; font-weight:800; color:#f8fafc; }
.modal-close { border:none; background:transparent; color:#cbd5e1; font-size:1.1rem; cursor:pointer; }
.modal-body { display:flex; flex-direction:column; gap:.9rem; }
.field, .field-grid { display:flex; flex-direction:column; gap:.4rem; }
.field label { font-size:.8rem; font-weight:700; color:rgba(226,232,240,.84); }
.field-grid { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:.75rem; }
.input--textarea { resize:vertical; min-height:92px; }
.modal-actions { display:flex; justify-content:flex-end; gap:.75rem; margin-top:1rem; }
.selected-warehouse {
  border-radius:1rem; padding:.85rem .95rem; background:rgba(15,23,42,.58); border:1px solid rgba(148,163,184,.22); color:#e2e8f0;
}
@media (max-width: 960px) {
  .almacenes-grid { grid-template-columns: 1fr; }
  .ubicaciones-panel { min-height:auto; }
}
@media (max-width: 720px) {
  .almacenes-header, .ubicaciones-panel__head { flex-direction:column; align-items:stretch; }
  .almacenes-header-actions { flex-direction:column; align-items:stretch; }
  .almacenes-search { min-width:0; }
  .field-grid { grid-template-columns:1fr; }
}
</style>
